import { apiClient, type PaginatedResponse } from '../../../api/client'
import buildApiUrl, { buildFetchUrl, buildPaginationParams, type PaginationOptions, fetchAllPages } from '../../../api/pagination'
import { type APIContract } from '../../../generated-types/models/APIContract'
import { APIContractFromJSON } from '../../../generated-types/models/APIContract'
import { type APIBill } from '../../../generated-types/models/APIBill'
import { APIBillFromJSON } from '../../../generated-types/models/APIBill'
import { ContractcontractsApi } from '../../../generated-types/apis/ContractcontractsApi'
import { ContractbillsApi } from '../../../generated-types/apis/ContractbillsApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import {
  createAddress,
  createFileInfo,
  createUser,
  getPKfromSelf,
  normalizeEditors,
  parseNestedObject,
  parseNestedObjectList,
} from '../../../utils/functions'
import { createArticle } from '../../Item/api/articleApi'
import { type Contract, type Bill } from '../utils/types'
import { createRequest } from '../../Request/api/requestApi'
import { APIContractStatusEnum, APIContractStatusEnumFromJSON } from '../../../generated-types/models/APIContractStatusEnum'
import { type PaginatedQueryParameters, type GenericFilterType } from '../../../api/hooks/utils/types'
import { type EntitySearchResponse } from '../../../hooks/useEntitySearch'

const log = new LogTool({ context: 'ContractAPI', enable: true, logLevel: 'warn' })

/**
 * Filter types for Contract queries
 */
export interface ContractFilters extends GenericFilterType {
  contract_number?: string
  offer_number?: string
  status?: string
  contract_status?: string
  offer?: string
  article?: string
  request?: string
  request_certificate?: string
  supplier?: string
  description?: string
  editors?: string
  created_at?: string
  last_modified_at?: string
  desired_delivery?: string
  latest_delivery?: string
  organization?: string
}

/**
 * Filter types for Bill queries
 */
export interface BillFilters extends GenericFilterType {
  bill_number?: string
  contract?: string
  amount?: string
  due_date?: string
  paid?: string
  editors?: string
  created_at?: string
  last_modified_at?: string
}

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Create API instances
 */
function getContractsApi(): ContractcontractsApi {
  return new ContractcontractsApi(getApiConfiguration())
}

function getBillsApi(): ContractbillsApi {
  return new ContractbillsApi(getApiConfiguration())
}

/**
 * Create Contract from API response
 */
export function createContract(contractResponse: APIContract): Contract {
  // Handle both camelCase (from TypeScript) and snake_case (from API/tests)
  const statusValue = contractResponse.contractStatus ?? (contractResponse as any).contract_status
  return {
    ...contractResponse,
    key: getPKfromSelf(contractResponse.self),
    editors: parseNestedObjectList(contractResponse.editors, createUser),
    files: parseNestedObjectList(contractResponse.files, createFileInfo),
    article: contractResponse.article ? createArticle(contractResponse.article) : '',
    request: contractResponse.request ? createRequest(contractResponse.request) : '',
    billingAddress: contractResponse.billingAddress ? createAddress(contractResponse.billingAddress) : '',
    shippingAddress: contractResponse.shippingAddress ? createAddress(contractResponse.shippingAddress) : '',
    read: contractResponse.read,
    // Use != null to properly handle 0 as a valid status value
    contractStatus: statusValue != null ? APIContractStatusEnumFromJSON(statusValue) : APIContractStatusEnum.NUMBER_0,
    organizationName:
      (contractResponse as any).rootAccountName ??
      (contractResponse as any).rootAccount?.name ??
      null,
  }
}

/**
 * Create Bill from API response
 */
export function createBill(billResponse: APIBill): Bill {
  return {
    ...billResponse,
    key: getPKfromSelf(billResponse.self),
    editors: parseNestedObjectList(billResponse.editors, createUser),
    files: parseNestedObjectList(billResponse.files, createFileInfo),
  }
}

/**
 * Convert Contract form input to API JSON
 */
export function inputToAPIContractJSON(input: any): APIContract {
  const editors = normalizeEditors(input.editors)

  // Extract contract status value if it's an object
  let contractStatus = input.contractStatus
  if (contractStatus && typeof contractStatus === 'object' && 'value' in contractStatus) {
    contractStatus = contractStatus.value
  }

  // Format dates to YYYY-MM-DD format
  let formattedDesiredDelivery
  if (input.desiredDelivery) {
    const date = new Date(input.desiredDelivery)
    formattedDesiredDelivery = date.toISOString().split('T')[0]
  }

  let formattedLatestDelivery
  if (input.latestDelivery) {
    const date = new Date(input.latestDelivery)
    formattedLatestDelivery = date.toISOString().split('T')[0]
  }

  // Convert editor UUIDs to full URLs if needed
  // normalizeEditors already extracts the 'self' values from user objects
  const editorUrls = editors?.map(editor => {
    // If it's already any kind of URL path (absolute or relative), return as-is
    if (editor.includes('/')) {
      return editor
    }
    // Only if it's a plain UUID (no slashes), convert to full URL
    const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/'
    const baseUrl = envApiUrl.endsWith('/') ? envApiUrl : envApiUrl + '/'
    return `${baseUrl}v1/accounts/users/${editor}/`
  })

  return {
    ...(input.contractNumber && { contract_number: input.contractNumber }),
    ...(input.supplier && { supplier: input.supplier?.self || input.supplier }),
    ...(input.articles && { 
      articles: Array.isArray(input.articles) 
        ? input.articles.map((a: any) => a?.self || a) 
        : []
    }),
    ...(input.description && { description: input.description }),
    ...(input.versionComment && { version_comment: input.versionComment }),
    ...(editorUrls && editorUrls.length >= 0 && { editors: editorUrls }),
    ...(input.comment !== undefined && { comment: input.comment }),
    ...(contractStatus !== undefined && { contract_status: contractStatus }),
    ...(formattedDesiredDelivery && { desired_delivery: formattedDesiredDelivery }),
    ...(formattedLatestDelivery && { latest_delivery: formattedLatestDelivery }),
    ...(input.priorityShipping !== undefined && { priority_shipping: input.priorityShipping }),
    ...(input.shippingAddress && { 
      shipping_address: input.shippingAddress?.self || input.shippingAddress 
    }),
    ...(input.billingAddress && { 
      billing_address: input.billingAddress?.self || input.billingAddress 
    }),
    ...(input.offer && { offer: input.offer?.self || input.offer }),
    ...(input.price && { price: input.price }),
  }
}

/**
 * Convert Bill form input to API JSON
 */
export function inputToAPIBillJSON(input: any): any {
  const editors = normalizeEditors(input.editors)

  // Format due_date to YYYY-MM-DD format
  let formattedDueDate
  if (input.dueDate) {
    const date = new Date(input.dueDate)
    formattedDueDate = date.toISOString().split('T')[0]
  }

  // Format created_at to YYYY-MM-DD format
  let formattedCreatedAt
  if (input.createdAt) {
    const date = new Date(input.createdAt)
    formattedCreatedAt = date.toISOString().split('T')[0]
  }

  // Convert editor UUIDs to full URLs if needed
  // normalizeEditors already extracts the 'self' values from user objects
  const editorUrls = editors?.map(editor => {
    // If it's already any kind of URL path (absolute or relative), return as-is
    if (editor.includes('/')) {
      return editor
    }
    // Only if it's a plain UUID (no slashes), convert to full URL
    const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/'
    const baseUrl = envApiUrl.endsWith('/') ? envApiUrl : envApiUrl + '/'
    return `${baseUrl}v1/accounts/users/${editor}/`
  })

  return {
    ...(input.amount && { amount: input.amount }),
    ...(formattedDueDate && { due_date: formattedDueDate }),
    ...(input.paid !== undefined && { paid: input.paid }),
    ...(input.contract && { 
      contract: typeof input.contract !== "string" && input.contract?.self 
        ? input.contract.self 
        : input.contract 
    }),
    ...(formattedCreatedAt && { created_at: formattedCreatedAt }),
    ...(input.files && { files: input.files }),
    ...(editorUrls && editorUrls.length >= 0 && { editors: editorUrls }),
    ...(input.self && { self: input.self }),
  }
}

/**
 * Fetch paginated contracts from API
 */
export async function fetchContracts(
  options: PaginatedQueryParameters<Contract, ContractFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<Contract>> {
  log.info('Fetching contracts with options:', options)

  // Use the new buildApiUrl for smart query support
  const url = buildApiUrl('v1/contract/contracts/', options as PaginatedQueryParameters<Contract, ContractFilters>, ids)
  
  const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(url)

  const contracts = response.results ? response.results.map((c: any) => createContract(APIContractFromJSON(c))) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: contracts,
  }
}

/**
 * Search contracts using Elasticsearch
 */
export async function searchContracts(
  options: PaginatedQueryParameters<Contract, ContractFilters> = {}
): Promise<EntitySearchResponse<Contract>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''
  
  const searchFilters = {
    ...filters,
    ...(hasFilters && !hasQ ? { q: '*' } : {}),
  }
  
  const url = buildApiUrl(
    'v1/search/contracts/contracts/',
    { filters: searchFilters }
  )
  return apiClient.get<EntitySearchResponse<Contract>>(url)
}

/**
 * Fetch a single contract by ID or URL
 */
export async function fetchContract(
  idOrUrl: string | number,
  expand?: string[]
): Promise<Contract> {
  log.info('Fetching contract:', idOrUrl)

  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `v1/contract/contracts/${idOrUrl}/`
    : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createContract(APIContractFromJSON(response))
}

/**
 * Fetch paginated bills from API
 */
export async function fetchBills(
  options: PaginatedQueryParameters<Bill, BillFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<Bill>> {
  log.info('Fetching bills with options:', options)

  // Use the new buildApiUrl for smart query support
  const url = buildApiUrl('v1/contract/bills/', options as PaginatedQueryParameters<Bill, BillFilters>, ids)
  
  const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(url)

  const bills = response.results ? response.results.map((b: any) => createBill(APIBillFromJSON(b))) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: bills,
  }
}

/**
 * Search bills using Elasticsearch
 */
export async function searchBills(
  options: PaginatedQueryParameters<Bill, BillFilters> = {}
): Promise<EntitySearchResponse<Bill>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''
  
  const searchFilters = {
    ...filters,
    ...(hasFilters && !hasQ ? { q: '*' } : {}),
  }
  
  const url = buildApiUrl(
    'v1/search/contracts/bills/',
    { filters: searchFilters }
  )
  return apiClient.get<EntitySearchResponse<Bill>>(url)
}

/**
 * Fetch a single bill by ID or URL
 */
export async function fetchBill(
  idOrUrl: string | number,
  expand?: string[]
): Promise<Bill> {
  log.info('Fetching bill:', idOrUrl)

  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `v1/contract/bills/${idOrUrl}/`
    : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createBill(APIBillFromJSON(response))
}

/**
 * Create a new contract
 */
export async function createContractApi(contractData: any): Promise<Contract> {
  log.info('Creating contract:', contractData)

  const json = inputToAPIContractJSON(contractData)
  const response = await getContractsApi().v1ContractContractsCreate({
    aPIContractRequest: json as any,
  })

  return createContract(response)
}

/**
 * Create a new bill
 */
export async function createBillApi(billData: any): Promise<Bill> {
  log.info('Creating bill:', billData)

  const json = inputToAPIBillJSON(billData)
  const response = await getBillsApi().v1ContractBillsCreate({
    aPIBillRequest: json,
  })

  return createBill(response)
}

/**
 * Update an existing contract
 */
export async function updateContractApi(
  idOrSelf: string | number,
  contractData: any
): Promise<Contract> {
  log.info('Updating contract:', idOrSelf, contractData)

  const json = inputToAPIContractJSON(contractData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getContractsApi().v1ContractContractsUpdate({
      id: idOrSelf,
      aPIContractRequest: json,
    })
    return createContract(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/contract/contracts/${idOrSelf}/`
  const response = await apiClient.put<any>(url, json)
  return createContract(response)
}

/**
 * Update an existing bill
 */
export async function updateBillApi(
  idOrSelf: string | number,
  billData: any
): Promise<Bill> {
  log.info('Updating bill:', idOrSelf, billData)

  const json = inputToAPIBillJSON(billData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getBillsApi().v1ContractBillsUpdate({
      id: idOrSelf,
      aPIBillRequest: json,
    })
    return createBill(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/contract/bills/${idOrSelf}/`
  const response = await apiClient.put<any>(url, json)
  return createBill(response)
}

/**
 * Patch an existing contract (partial update)
 */
export async function patchContractApi(
  idOrSelf: string | number,
  contractData: Partial<any>
): Promise<Contract> {
  log.info('Patching contract:', idOrSelf, contractData)

  const json = inputToAPIContractJSON(contractData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getContractsApi().v1ContractContractsPartialUpdate({
      id: idOrSelf,
      aPIPatchedContractRequest: json,
    })
    return createContract(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/contract/contracts/${idOrSelf}/`
  const response = await apiClient.patch<any>(url, json)
  return createContract(response)
}

/**
 * Patch an existing bill (partial update)
 */
export async function patchBillApi(
  idOrSelf: string | number,
  billData: Partial<any>
): Promise<Bill> {
  log.info('Patching bill:', idOrSelf, billData)

  const json = inputToAPIBillJSON(billData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getBillsApi().v1ContractBillsPartialUpdate({
      id: idOrSelf,
      aPIPatchedBillRequest: json,
    })
    return createBill(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/contract/bills/${idOrSelf}/`
  const response = await apiClient.patch<any>(url, json)
  return createBill(response)
}

/**
 * Delete a contract
 */
export async function deleteContractApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting contract:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await getContractsApi().v1ContractContractsDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/contract/contracts/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Delete a bill
 */
export async function deleteBillApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting bill:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await getBillsApi().v1ContractBillsDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/contract/bills/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Delete multiple contracts
 */
export async function deleteContractsApi(contracts: Contract[]): Promise<void> {
  log.info('Deleting contracts:', contracts.length)

  const deletePromises = contracts.map(contract => deleteContractApi(contract.self))
  await Promise.all(deletePromises)
}

/**
 * Delete multiple bills
 */
export async function deleteBillsApi(bills: Bill[]): Promise<void> {
  log.info('Deleting bills:', bills.length)

  const deletePromises = bills.map(bill => deleteBillApi(bill.self))
  await Promise.all(deletePromises)
}

/**
 * Upload file to contract
 */
export async function uploadContractFile(
  contractSelf: string,
  file: File
): Promise<void> {
  log.info('Uploading file to contract:', contractSelf)

  const formData = new FormData()
  formData.append('contract', contractSelf)
  formData.append('document', file)

  await apiClient.post(`v1/${contractSelf}files/`, formData, {
    'Content-Type': 'multipart/form-data',
  }, true)
}

/**
 * Upload file to bill
 */
export async function uploadBillFile(
  billSelf: string,
  file: File
): Promise<void> {
  log.info('Uploading file to bill:', billSelf)

  const formData = new FormData()
  formData.append('bill', billSelf)
  formData.append('document', file)

  await apiClient.post(`v1/${billSelf}files/`, formData, {
    'Content-Type': 'multipart/form-data',
  }, true)
}
